import type { CastingProfileResponse } from '@innovationdepartment/proxima-sdk-axios';
import { useToaster } from '@innovationdepartment/proxima-ui';
import { useShowSpinner } from 'hooks';
import { useCallback, useEffect, useRef, useState } from 'react';

import useAvatarManagementApi from '../useAvatarManagementApi';
import type { CastingMetadata } from './CastingProfile.types';
import CastingProfileView from './CastingProfile.View';
import useCastingProfileForm from './useCastingProfileForm';

const CastingProfileContainer = () => {
  const api = useAvatarManagementApi();
  const { showToaster } = useToaster();
  const showToasterRef = useRef(showToaster);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<CastingMetadata | null>(null);
  const [profile, setProfile] = useState<CastingProfileResponse | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmingSave, setConfirmingSave] = useState(false);

  useShowSpinner({ show: loading });

  const form = useCastingProfileForm(profile);
  const { reset } = form;

  const apiRef = useRef(api);
  apiRef.current = api;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    const [metadataResult, profileResult] = await Promise.all([
      apiRef.current.getMetadata(),
      apiRef.current.getCastingProfile()
    ]);

    const error = metadataResult.error ?? profileResult.error;
    if (error != null) {
      setLoadError(error);
      setLoading(false);
      return;
    }

    setMetadata(metadataResult.data ?? null);
    setProfile(profileResult.data ?? null);
    reset(profileResult.data ?? null);
    setLoading(false);
  }, [reset]);

  useEffect(() => {
    load();
  }, [load]);

  const refetchAfterSave = useCallback(async () => {
    const profileResult = await apiRef.current.getCastingProfile();

    if (profileResult.error == null) {
      setProfile(profileResult.data ?? null);
      reset(profileResult.data ?? null);
    } else {
      showToasterRef.current({ message: profileResult.error, variant: 'error' });
    }
  }, [reset]);

  const handleSave = useCallback(async () => {
    // Close the confirmation up front; the disabled outer Save button reflects progress.
    setConfirmingSave(false);
    setSaving(true);

    const result = await apiRef.current.updateCastingProfile(form.toRequest());

    if (result.success) {
      showToasterRef.current({ message: 'Casting profile saved', variant: 'success' });
      await refetchAfterSave();
    } else {
      showToasterRef.current({ message: result.error, variant: 'error' });
    }

    setSaving(false);
  }, [form, refetchAfterSave]);

  return (
    <CastingProfileView
      loading={loading}
      loadError={loadError}
      metadata={metadata}
      profileExists={profile !== null}
      form={form}
      saving={saving}
      confirmingSave={confirmingSave}
      onSaveRequest={() => setConfirmingSave(true)}
      onSaveConfirm={handleSave}
      onSaveCancel={() => setConfirmingSave(false)}
      onRetry={load}
    />
  );
};

export default CastingProfileContainer;
